// src/components/onboarding/ProfileSetupModal.js
//
// First-connect nudge (tier 2 of onboarding). Shown ONCE ever, when
// WalletContext provisions a brand-new user (isNew === true) and the
// connect wasn't purchase-initiated. Fully skippable — the silently
// provisioned user_xxxxxx identity is a valid permanent state.
//
// Now includes a DEFAULT AVATAR: a DiceBear croodle seeded from the user,
// with a shuffle button to re-roll. Saved to users.avatar_url alongside
// the username, so the same face follows the user into chat, action
// requests, and everywhere else. Replaceable later with a real photo on
// the Profile page. Skippers keep no stored avatar — display surfaces
// fall back to the deterministic croodle via resolveAvatar(), so they
// still look consistent everywhere.

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Check, Loader2, Sparkles, Dices } from 'lucide-react';
import { userService } from '../../services/UserService';
import { dylanUrl, randomAvatarSeed, isDefaultAvatar } from '../../lib/avatars';

// Same rules as ProfilePage — keep in sync (or extract to a shared util).
const validateUsername = (value) => {
  if (!value) return 'Username is required';
  if (value.length < 3) return 'Min 3 chars';
  if (value.length > 20) return 'Max 20 chars';
  if (!/^[a-zA-Z0-9_]+$/.test(value)) return 'Alphanumeric and underscores only';
  return '';
};

const ProfileSetupModal = ({ user, onClose, onSaved }) => {
  // Prefill with the provisioned defaults so the field is never empty —
  // editing a suggestion is lower friction than facing a blank input.
  const [username, setUsername] = useState(user?.username || '');
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  // Avatar seed: matches provisioning (wallet-seeded croodle) so the modal
  // opens showing the SAME doodle the user was just given; shuffle re-rolls.
  // A real uploaded photo (non-default avatar_url) wins and hides shuffle.
  const [avatarSeed, setAvatarSeed] = useState(
    () => user?.wallet_address || user?.id || randomAvatarSeed()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const hasRealAvatar = !!user?.avatar_url && !isDefaultAvatar(user.avatar_url);
  const avatarPreview = hasRealAvatar ? user.avatar_url : dylanUrl(avatarSeed);

  const usernameError = validateUsername(username);
  const canSave = !usernameError && !saving;

  const handleSave = async () => {
    if (!canSave || !user?.id) return;
    setSaving(true);
    setError('');

    const avatar_url = hasRealAvatar ? user.avatar_url : dylanUrl(avatarSeed);

    // If you have a userService.updateProfile(...) that ProfilePage's
    // Save uses, call that instead — one write path is better than two.
    // users rows are server-owned: save through user-profile (own row only).
    const { error: updateErr } = await userService.updateProfile(user.wallet_address, {
      username: username.trim(),
      display_name: displayName.trim() || null,
      avatar_url,
    });

    setSaving(false);

    if (updateErr) {
      if (updateErr.code === 'username_taken' || updateErr.code === '23505') {
        setError('That username is taken — try another');
      } else {
        setError(updateErr.message || 'Could not save profile');
      }
      return;
    }

    onSaved?.({
      username: username.trim(),
      display_name: displayName.trim() || null,
      avatar_url,
    });
    onClose();
  };

  const inputClassName =
    'w-full h-12 px-4 bg-[#141414] border border-white/[0.06] rounded-[16px] text-zinc-200 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-white/20 focus:bg-[#1A1A1A] transition-colors shadow-inner';

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        className="relative w-full max-w-sm overflow-hidden rounded-[28px] border border-white/[0.08] bg-gradient-to-b from-[#1E1E1E] to-[#0F0F0F] shadow-[0_32px_100px_rgba(0,0,0,0.5)]"
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[#A3E635]/[0.04] blur-[100px]" />

        {/* Header */}
        <div className="relative z-10 flex items-start justify-between px-6 pt-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-[16px] border border-[#A3E635]/20 bg-[#A3E635]/10">
              <Sparkles size={18} className="text-[#A3E635]" />
            </div>
            <div>
              <h3 className="text-[16px] font-bold tracking-tight text-white">Welcome to Lunexx</h3>
              <p className="text-[13px] text-zinc-500">Pick a name people will see in rooms</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/[0.06] bg-white/[0.03] text-zinc-500 transition-colors hover:bg-white/[0.08] hover:text-white"
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="relative z-10 space-y-4 px-6 py-6">

          {/* Avatar preview + shuffle */}
          <div className="flex flex-col items-center gap-2 pb-1">
            <div className="relative">
              <div className="h-20 w-20 overflow-hidden rounded-full border border-white/10 bg-white/[0.04] shadow-inner">
                <img
                  src={avatarPreview}
                  alt="Your avatar"
                  className="h-full w-full object-cover"
                />
              </div>
              {!hasRealAvatar && (
                <button
                  type="button"
                  onClick={() => setAvatarSeed(randomAvatarSeed())}
                  title="Shuffle avatar"
                  className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-[#1E1E1E] text-zinc-400 shadow-lg transition-all hover:scale-105 hover:text-white active:scale-95"
                >
                  <Dices size={14} />
                </button>
              )}
            </div>
            {!hasRealAvatar && (
              <p className="text-[11px] text-zinc-600">
                Your doodle — shuffle it, or swap in a photo later in Profile
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block pl-1 text-[12px] font-semibold text-zinc-400">Username</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-zinc-600">@</span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="satoshi_zero"
                className={`${inputClassName} pl-9`}
                maxLength={20}
                autoFocus
              />
            </div>
            {username && usernameError && (
              <p className="mt-1.5 pl-1 text-[12px] text-red-400">{usernameError}</p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block pl-1 text-[12px] font-semibold text-zinc-400">
              Display name <span className="font-medium text-zinc-600">(optional)</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Satoshi"
              className={inputClassName}
              maxLength={40}
            />
          </div>

          {error && (
            <p className="pl-1 text-[12px] text-red-400">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="relative z-10 flex items-center justify-between border-t border-white/[0.04] px-6 py-4">
          <button
            onClick={onClose}
            className="text-[13px] font-medium text-zinc-500 transition-colors hover:text-white"
          >
            Set up later
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="flex h-11 items-center gap-2 rounded-[14px] bg-white px-6 text-[13px] font-bold text-black transition-all hover:bg-zinc-200 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} strokeWidth={2.5} />}
            Save
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ProfileSetupModal;
